import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api-client.js';

interface Customer {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  city?: string;
  state?: string;
  source: string;
  deal_count: number;
  latest_deal_status?: string;
  last_contact_at?: string;
  created_at: string;
}

const SOURCE_LABELS: Record<string, string> = {
  walk_in: 'Walk-in',
  phone: 'Phone',
  web: 'Website',
  referral: 'Referral',
  facebook: 'Facebook',
  craigslist: 'Craigslist',
  autotrader: 'AutoTrader',
  cargurus: 'CarGurus',
  other: 'Other',
};

export default function CustomersPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Customer list
  const { data: listData } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => api.get<Customer[]>(`/customers?search=${encodeURIComponent(search)}&limit=100`),
  });

  const customers = listData?.data ?? [];

  // Customer detail
  const { data: detailData } = useQuery({
    queryKey: ['customer', selectedId],
    queryFn: () => api.get<any>(`/customers/${selectedId}`),
    enabled: !!selectedId,
  });

  const customer = detailData?.data;

  // Create customer
  const createMut = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post('/customers', data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['customers'] }); setShowAdd(false); },
  });

  if (selectedId && customer) {
    return <CustomerDetail customer={customer} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="page">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Customers</h2>
        <button onClick={() => setShowAdd(true)} className="btn-primary text-sm px-4 py-2">
          + Add Customer
        </button>
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Search by name, email, or phone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="w-full bg-surface-800 border border-surface-700 rounded-lg px-4 py-3 mb-4 text-white placeholder-surface-500"
      />

      {/* Add Customer Modal */}
      {showAdd && (
        <AddCustomerForm
          onSubmit={(data) => createMut.mutate(data)}
          onCancel={() => setShowAdd(false)}
          loading={createMut.isPending}
        />
      )}

      {/* Customer List */}
      <div className="space-y-2">
        {customers.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelectedId(c.id)}
            className="w-full card p-4 text-left hover:bg-surface-700 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">{c.name}</p>
                <p className="text-sm text-surface-500">
                  {c.phone ?? c.email ?? 'No contact info'} {c.city && c.state ? `| ${c.city}, ${c.state}` : ''}
                </p>
              </div>
              <div className="text-right">
                <span className="text-xs px-2 py-1 rounded-full bg-surface-700 text-surface-400">
                  {SOURCE_LABELS[c.source] ?? c.source}
                </span>
                {c.deal_count > 0 && (
                  <p className="text-xs text-brand-400 mt-1">{c.deal_count} deal{c.deal_count > 1 ? 's' : ''}</p>
                )}
              </div>
            </div>
          </button>
        ))}
        {customers.length === 0 && (
          <p className="text-center text-surface-500 py-8">No customers yet. Add your first customer to get started.</p>
        )}
      </div>
    </div>
  );
}

// ── Add Customer Form ────────────────────────────
function AddCustomerForm({
  onSubmit,
  onCancel,
  loading,
}: {
  onSubmit: (data: Record<string, unknown>) => void;
  onCancel: () => void;
  loading: boolean;
}) {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', address_line1: '', city: '', state: '', zip: '',
    drivers_license: '', dl_state: '', source: 'walk_in', notes: '',
  });

  return (
    <div className="card p-6 mb-4 border border-brand-500/30">
      <h3 className="text-lg font-semibold mb-4">New Customer</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input placeholder="Full Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
        <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
        <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
        <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="input">
          {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input placeholder="Address" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} className="input" />
        <input placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input" />
        <input placeholder="State" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="input" />
        <input placeholder="ZIP" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="input" />
        <input placeholder="Driver's License #" value={form.drivers_license} onChange={(e) => setForm({ ...form, drivers_license: e.target.value })} className="input" />
        <input placeholder="DL State" value={form.dl_state} onChange={(e) => setForm({ ...form, dl_state: e.target.value })} className="input" />
      </div>
      <textarea placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input mt-3 h-20" />
      <div className="flex gap-3 mt-4">
        <button onClick={() => onSubmit(form)} disabled={!form.name || loading} className="btn-primary px-6 py-2 disabled:opacity-50">
          {loading ? 'Saving...' : 'Save Customer'}
        </button>
        <button onClick={onCancel} className="btn-secondary px-4 py-2">Cancel</button>
      </div>
    </div>
  );
}

// ── Customer Detail ────────────────────────────────
function CustomerDetail({ customer, onBack }: { customer: any; onBack: () => void }) {
  const qc = useQueryClient();

  const addInteraction = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.post(`/customers/${customer.id}/interactions`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['customer', customer.id] }),
  });

  const [noteType, setNoteType] = useState('note');
  const [noteText, setNoteText] = useState('');

  return (
    <div className="page">
      <button onClick={onBack} className="text-brand-400 text-sm mb-4">&larr; Back to Customers</button>

      <div className="card p-6 mb-4">
        <h2 className="text-xl font-bold">{customer.name}</h2>
        <div className="grid grid-cols-2 gap-3 mt-3 text-sm">
          {customer.phone && <div><span className="text-surface-500">Phone:</span> {customer.phone}</div>}
          {customer.email && <div><span className="text-surface-500">Email:</span> {customer.email}</div>}
          {customer.city && <div><span className="text-surface-500">Location:</span> {customer.city}, {customer.state} {customer.zip}</div>}
          {customer.drivers_license && <div><span className="text-surface-500">DL:</span> {customer.drivers_license} ({customer.dl_state})</div>}
          <div><span className="text-surface-500">Source:</span> {SOURCE_LABELS[customer.source] ?? customer.source}</div>
          <div><span className="text-surface-500">Added:</span> {new Date(customer.created_at).toLocaleDateString()}</div>
        </div>
      </div>

      {/* Deals */}
      {customer.deals?.length > 0 && (
        <div className="mb-4">
          <h3 className="text-lg font-semibold mb-2">Deals</h3>
          <div className="space-y-2">
            {customer.deals.map((d: any) => (
              <Link key={d.id} to={`/deals/${d.id}`} className="card p-4 block hover:bg-surface-700 transition-colors">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium">{d.year} {d.make} {d.model}</p>
                    <p className="text-sm text-surface-500">{d.deal_number} | {d.vin}</p>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      d.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                      d.status === 'lost' ? 'bg-red-500/20 text-red-400' :
                      'bg-yellow-500/20 text-yellow-400'
                    }`}>{d.status}</span>
                    <p className="text-sm font-medium mt-1">${((d.sale_price_cents ?? 0) / 100).toLocaleString()}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick Note / Interaction */}
      <div className="card p-4 mb-4">
        <h3 className="text-sm font-semibold mb-2">Log Interaction</h3>
        <div className="flex gap-2 mb-2">
          {['note', 'call', 'text', 'email', 'visit', 'test_drive', 'follow_up'].map((t) => (
            <button
              key={t}
              onClick={() => setNoteType(t)}
              className={`text-xs px-2 py-1 rounded ${noteType === t ? 'bg-brand-500 text-white' : 'bg-surface-700 text-surface-400'}`}
            >{t.replace('_', ' ')}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            placeholder="Add a note..."
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            className="input flex-1"
          />
          <button
            onClick={() => {
              if (noteText.trim()) {
                addInteraction.mutate({ interaction_type: noteType, notes: noteText });
                setNoteText('');
              }
            }}
            className="btn-primary px-4 py-2 text-sm"
          >Add</button>
        </div>
      </div>

      {/* Interaction Timeline */}
      {customer.interactions?.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-2">Activity</h3>
          <div className="space-y-2">
            {customer.interactions.map((i: any) => (
              <div key={i.id} className="card p-3">
                <div className="flex justify-between items-start">
                  <div>
                    <span className="text-xs px-2 py-0.5 rounded bg-surface-700 text-surface-400 mr-2">
                      {i.interaction_type}
                    </span>
                    {i.user_name && <span className="text-xs text-surface-500">{i.user_name}</span>}
                  </div>
                  <span className="text-xs text-surface-600">{new Date(i.created_at).toLocaleDateString()}</span>
                </div>
                {i.notes && <p className="text-sm mt-1">{i.notes}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
